import { useMemo, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { instance } from "api/apiBase";
import { toast } from "react-toastify";
import { Button, ButtonSize, ButtonVariant, Checkbox } from "shared/components";
import { Card } from "shared/components/Card";
import { getErrorMessage } from "utils/error";
import { Step } from "../../types";

type ConsentLender = {
  id: string;
  name: string;
  consent_documents?: Array<{ id: string; title: string; file_url: string }>;
};

export const BoomFinanceConsents = ({
  applicantId,
  applicationId,
  step,
  onNextStep
}: {
  applicantId: number;
  applicationId: number;
  step: Step;
  onNextStep: () => void;
}) => {
  const verificationId = step.id;
  const [acceptedConsents, setAcceptedConsents] = useState<
    Record<string, boolean>
  >({});

  const items = useMemo(() => {
    const lenders = (step.prefilled_data?.lenders ?? []) as ConsentLender[];
    return lenders.flatMap((lender) =>
      (lender.consent_documents ?? []).map((doc) => ({
        id: doc.id,
        title: doc.title,
        description: `Provided by ${lender.name}`,
        url: doc.file_url
      }))
    );
  }, [step.prefilled_data]);

  const acceptConsents = useMutation({
    mutationKey: [
      "BOOM_FINANCE_ACCEPT_CONSENTS",
      applicationId,
      verificationId
    ],
    mutationFn: (disclosureIds: string[]) =>
      instance.post(
        `/portal/v1/applications/${applicationId}/verifications/${verificationId}/boom_finance/consents`,
        { disclosure_ids: disclosureIds, applicant_id: applicantId }
      ),
    onSuccess: onNextStep,
    onError: (error) =>
      toast.error(getErrorMessage(error) || "Could not record consents")
  });

  const allAccepted =
    items.length > 0 && items.every((i) => acceptedConsents[i.id]);

  return (
    <div className="h-[calc(80vh-72px)] w-full flex flex-col">
      <div className="h-full w-full flex-1 py-6 px-12 overflow-y-auto space-y-3">
        <div className="mb-6 space-y-1">
          <h1 className="font-bold text-xl text-content-1000">
            Accept consents
          </h1>
          <p className="text-sm text-content-400">
            Before we send the application, please review the following
          </p>
        </div>

        {items.map((item) => (
          <Card key={item.id} className="p-4 space-y-1">
            <h3 className="text-base font-bold">{item.title}</h3>
            <p className="text-sm text-content-400">{item.description}</p>
            <a
              href={item.url}
              target="_blank"
              rel="noreferrer"
              className="text-primary-500 inline-block text-sm font-medium underline disabled:opacity-50"
            >
              View document
            </a>
            <hr className="border-alpha-40 !my-3" />
            <Checkbox
              id={item.id}
              checked={Boolean(acceptedConsents[item.id])}
              onChange={() =>
                setAcceptedConsents((prev) => ({
                  ...prev,
                  [item.id]: !prev[item.id]
                }))
              }
              label="The applicant has read and accepts this disclosure"
            />
          </Card>
        ))}
      </div>

      <div className="border-t border-alpha-100 bg-white">
        <div className="w-full flex justify-end px-6 py-4 gap-2">
          <Button
            size={ButtonSize.LG}
            variant={ButtonVariant.PRIMARY}
            disabled={!allAccepted || acceptConsents.isPending}
            onClick={() => acceptConsents.mutate(items.map((i) => i.id))}
            className="m-auto w-[550px]"
          >
            Continue
          </Button>
        </div>
      </div>
    </div>
  );
};
